import { nanoid } from 'nanoid'
import { ANONYMOUS_ID_COOKIE_NAME, COOKIE_CONSENT_COOKIE_NAME } from 'lib/hypertune/constants'
import Cookies from 'js-cookie'
import { NextRequest, NextResponse } from 'next/server'
import { RequestCookies, ResponseCookies } from 'next/dist/compiled/@edge-runtime/cookies'
import { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies'

export function getAnonymousId(serverCookies?: ReadonlyRequestCookies): string | null {
  return (serverCookies ? serverCookies.get(ANONYMOUS_ID_COOKIE_NAME)?.value : Cookies.get(ANONYMOUS_ID_COOKIE_NAME)) || null
}

export function setAnonymousIdIfNeeded(req?: NextRequest): void {
  const isServer = !!req
  const anonymousIdCookie = isServer ? req?.cookies?.get(ANONYMOUS_ID_COOKIE_NAME)?.value : Cookies.get(ANONYMOUS_ID_COOKIE_NAME)
  const consentCookie = isServer ? req?.cookies?.get(COOKIE_CONSENT_COOKIE_NAME)?.value : Cookies.get(COOKIE_CONSENT_COOKIE_NAME)

  if (anonymousIdCookie) {
    return
  }

  const consentGiven = consentCookie?.toLowerCase().includes('necessary:true')
  if (process.env.VERCEL_ENV === 'production' && consentGiven) {
    return
  }

  const newAnonymousId = nanoid()
  const response = req ? NextResponse.next() : undefined

  if (response) {
    response?.cookies?.set({
      name: ANONYMOUS_ID_COOKIE_NAME,
      value: newAnonymousId,
      domain: process.env.NODE_ENV === 'development' ? undefined : 'boltenergie.be',
      path: '/'
    })
  }
  Cookies.set(ANONYMOUS_ID_COOKIE_NAME, newAnonymousId, { path: '/' })

  if (req && response) {
    applySetCookie(req, response)
  }
}

/**
 * Copy cookies from the Set-Cookie header of the response to the Cookie header
 * of the request so that it will appear to SSR/RSC as if the user already has
 * the new cookies.
 */
function applySetCookie(req: NextRequest, res: NextResponse): void {
  // 1. Parse Set-Cookie header from the response
  const setCookies = new ResponseCookies(res.headers)

  // 2. Construct updated Cookie header for the request
  const newReqHeaders = new Headers(req.headers)
  const newReqCookies = new RequestCookies(newReqHeaders)
  setCookies.getAll().forEach((cookie) => newReqCookies.set(cookie))

  // 3. Set up the "request header overrides" on a dummy response
  // See https://github.com/vercel/next.js/pull/41380
  // NextResponse.next will set x-middleware-override-headers and
  // x-middleware-request-* headers
  const dummyRes = NextResponse.next({ request: { headers: newReqHeaders } })

  // 4. Copy the "request header overrides" headers from the dummy response to
  // the real response
  dummyRes.headers.forEach((value, key) => {
    if (key === 'x-middleware-override-headers' || key.startsWith('x-middleware-request-')) {
      res.headers.set(key, value)
    }
  })
}
